import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/features/auth/context/AuthContext';
import { usePortalConfig } from '@shared/config';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { User, LogOut, Search, Clock, Settings } from 'lucide-react';
import { ModeToggle } from '@shared/components/mode-toggle';

export function Header() {
  const { user, signOut } = useAuth();
  const { branding, assets } = usePortalConfig();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on an auth page (login, register, forgot-password, reset-password)
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signOut fails, the AuthContext now forces local cleanup
      // So we can still navigate away safely
    } finally {
      // Always navigate to home, even if signOut had issues
      navigate('/');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md shadow-sm">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="flex h-16 items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink">
            <div className="w-[50px] h-10 flex items-center justify-start flex-shrink-0">
              <img
                src={assets.logoSmall}
                alt={branding.name}
                className={`${assets.logoHeight || 'max-h-10'} object-contain`}
              />
            </div>
            <span className="hidden min-[400px]:inline text-lg sm:text-3xl lg:text-4xl font-semibold font-cormorant text-secondary dark:text-foreground whitespace-nowrap">{branding.name}</span>
          </Link>

          <nav className="flex items-center space-x-1.5 sm:space-x-4">
            {user ? (
              <>
                {!isAuthPage && (
                  <>
                    <Link to="/recent">
                      <Button variant="ghost" className="flex items-center gap-2 text-foreground px-2 sm:px-4">
                        <Clock className="h-4 w-4" />
                        <span className="hidden sm:inline">Recent</span>
                      </Button>
                    </Link>
                    <Link to="/search">
                      <Button variant="ghost" className="flex items-center gap-2 text-foreground px-2 sm:px-4">
                        <Search className="h-4 w-4" />
                        <span className="hidden sm:inline">Zoeken</span>
                      </Button>
                    </Link>
                  </>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-foreground">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      Instellingen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Uitloggen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {isAuthPage ? (
                  <>
                    {location.pathname !== '/login' && (
                      <Link to="/login">
                        <Button variant="ghost" className="text-foreground text-sm px-2 sm:px-4 sm:text-base">Inloggen</Button>
                      </Link>
                    )}
                    {location.pathname !== '/register' && (
                      <Link to="/register">
                        <Button className="text-sm px-3 sm:px-4 sm:text-base">Registreren</Button>
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <Link to="/login">
                      <Button variant="ghost" className="text-foreground text-sm px-2 sm:px-4 sm:text-base">Inloggen</Button>
                    </Link>
                    <Link to="/register">
                      <Button className="text-sm px-3 sm:px-4 sm:text-base">Registreren</Button>
                    </Link>
                  </>
                )}
              </>
            )}
            <ModeToggle />
          </nav>
        </div>
      </div>
    </header>
  );
}